import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { 
  Users, 
  Building2, 
  CheckCircle, 
  XCircle, 
  Clock, 
  Activity,
  Database,
  CreditCard,
  Shield,
  LogOut,
  Menu,
  X,
  Package,
  Calendar,
  UserCircle,
  TrendingUp,
  BarChart3,
  Anchor
} from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import SystemHealthView from '../components/SystemHealthView';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SuperAdminDashboard = () => {
  const navigate = useNavigate();
  const [activeView, setActiveView] = useState('dashboard');
  const [sidebarOpen, setSidebarOpen] = useState(false); // Collapsed by default
  const [organizations, setOrganizations] = useState([]);
  const [platformHealth, setPlatformHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [dbConnectionString, setDbConnectionString] = useState('');

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setSidebarOpen(true);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    fetchOrganizations();
    fetchPlatformHealth();
  }, []);

  const fetchOrganizations = async () => {
    try {
      const response = await fetch(`${API}/super-admin/organizations`);
      const data = await response.json();
      setOrganizations(data.organizations || []);
    } catch (error) {
      console.error('Error fetching organizations:', error);
      toast.error('Failed to load organizations');
    } finally {
      setLoading(false);
    }
  };

  const fetchPlatformHealth = async () => {
    try {
      const response = await fetch(`${API}/super-admin/platform-health`);
      const data = await response.json();
      setPlatformHealth(data);
    } catch (error) {
      console.error('Error fetching platform health:', error);
    }
  };

  const approveOrganization = async (orgId) => {
    if (!dbConnectionString) {
      toast.error('Please provide a database connection string');
      return;
    }

    try {
      console.log('Approving org:', orgId, 'with DB string:', dbConnectionString);
      
      const response = await fetch(`${API}/super-admin/organizations/${orgId}/approve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ dbConnectionString })
      });

      const data = await response.json();
      console.log('Approval response:', data);

      if (response.ok) {
        toast.success('Organization approved successfully!');
        setSelectedOrg(null);
        setDbConnectionString('');
        fetchOrganizations();
      } else {
        console.error('Approval failed:', data);
        toast.error(data.detail || 'Failed to approve organization');
      }
    } catch (error) {
      console.error('Error approving organization:', error);
      toast.error('An error occurred: ' + error.message);
    }
  };

  const rejectOrganization = async (orgId) => {
    if (!window.confirm('Are you sure you want to reject this organization? This cannot be undone.')) {
      return;
    }

    try {
      const response = await fetch(`${API}/super-admin/organizations/${orgId}/reject`, {
        method: 'POST'
      });

      if (response.ok) {
        toast.success('Organization rejected');
        fetchOrganizations();
      } else {
        toast.error('Failed to reject organization');
      }
    } catch (error) {
      console.error('Error rejecting organization:', error);
      toast.error('An error occurred');
    }
  };

  const suspendOrganization = async (orgId) => {
    if (!window.confirm('Are you sure you want to suspend this organization?')) {
      return;
    }

    try {
      const response = await fetch(`${API}/super-admin/organizations/${orgId}/suspend`, {
        method: 'POST'
      });

      if (response.ok) {
        toast.success('Organization suspended');
        fetchOrganizations();
      } else {
        toast.error('Failed to suspend organization');
      }
    } catch (error) {
      console.error('Error suspending organization:', error);
      toast.error('An error occurred');
    }
  };

  const handleLogout = () => {
    localStorage.clear();
    toast.success('Logged out successfully');
    navigate('/login');
  };

  const pendingOrgs = organizations.filter(org => org.status === 'pending');
  const activeOrgs = organizations.filter(org => org.status === 'active');
  const suspendedOrgs = organizations.filter(org => org.status === 'suspended');

  const totalTeamMembers = activeOrgs.reduce((sum, org) => sum + (org.stats?.team_members || 0), 0);
  const totalAssets = activeOrgs.reduce((sum, org) => sum + (org.stats?.assets || 0), 0);
  const totalBookings = activeOrgs.reduce((sum, org) => sum + (org.stats?.trips || 0), 0);

  const navItems = [
    { id: 'dashboard', label: 'Dashboard', icon: BarChart3 },
    { id: 'accounts', label: 'Asset Accounts', icon: Building2 },
    { id: 'health', label: 'System Health', icon: Activity },
  ];

  return (
    <div className="flex h-screen bg-gray-50">
      {/* Sidebar */}
      <aside
        className={`${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        } fixed lg:static inset-y-0 left-0 z-50 w-64 bg-white border-r border-gray-200 flex flex-col transition-transform duration-300 ease-in-out`}
      >
        {/* Sidebar Header */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-2 rounded-lg">
              <Shield className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="font-bold text-lg text-gray-900">Super Admin</h2>
              <p className="text-xs text-gray-500">Platform Control</p>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-2 overflow-y-auto">
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = activeView === item.id;
            return (
              <button
                key={item.id}
                onClick={() => setActiveView(item.id)}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
                  isActive
                    ? 'text-white bg-blue-600'
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                <Icon className="w-5 h-5" />
                <span className="font-medium">{item.label}</span>
                {item.id === 'accounts' && pendingOrgs.length > 0 && (
                  <Badge className="ml-auto bg-yellow-500">{pendingOrgs.length}</Badge>
                )}
              </button>
            );
          })}
        </nav>

        {/* Sidebar Footer */}
        <div className="p-4 border-t border-gray-200 space-y-2">
          <Button
            onClick={handleLogout}
            variant="outline"
            className="w-full justify-start border-red-500 text-red-500 hover:bg-red-50"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Logout
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 flex flex-col overflow-hidden">
        {/* Top Bar */}
        <header className="bg-white border-b border-gray-200 p-4">
          <div className="flex items-center justify-between gap-4">
            <Button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              variant="outline"
              size="sm"
              className="p-2"
            >
              {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </Button>

            <div className="flex items-center gap-2">
              <Anchor className="w-6 h-6 text-blue-600" />
              <h1 className="text-xl font-bold text-gray-900">BookingFlows Platform</h1>
            </div>

            <div className="text-sm text-gray-600">
              {new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
            </div>
          </div>
        </header>

        {/* Content Area */}
        <div className="flex-1 overflow-y-auto p-6 pb-32">
          {/* Dashboard View */}
          {activeView === 'dashboard' && (
            <div className="space-y-6">
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-1">Platform Overview</h2>
                <p className="text-gray-600">Monitor and manage all asset accounts</p>
              </div>

              {/* Stats Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-gray-600">Total Organizations</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center justify-between">
                      <span className="text-3xl font-bold">{organizations.length}</span>
                      <Building2 className="w-8 h-8 text-blue-600" />
                    </div>
                    <p className="text-xs text-gray-500 mt-2">
                      {activeOrgs.length} active, {pendingOrgs.length} pending
                    </p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-gray-600">Platform Health</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center justify-between">
                      <span className="text-3xl font-bold text-green-600">
                        {platformHealth ? `${platformHealth.healthPercentage}%` : '-'}
                      </span>
                      <Activity className="w-8 h-8 text-green-600" />
                    </div>
                    <p className="text-xs text-gray-500 mt-2">All systems operational</p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-gray-600">Total TeamMembers</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center justify-between">
                      <span className="text-3xl font-bold">{totalTeamMembers}</span>
                      <UserCircle className="w-8 h-8 text-purple-600" />
                    </div>
                    <p className="text-xs text-gray-500 mt-2">Across all organizations</p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-gray-600">Total Assets</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center justify-between">
                      <span className="text-3xl font-bold">{totalAssets}</span>
                      <Package className="w-8 h-8 text-cyan-600" />
                    </div>
                    <p className="text-xs text-gray-500 mt-2">Resources-wide count</p>
                  </CardContent>
                </Card>
              </div>

              {/* Recent Activity */}
              <Card>
                <CardHeader>
                  <CardTitle>Recent Activity</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {pendingOrgs.slice(0, 3).map(org => (
                      <div key={org.id} className="flex items-center justify-between p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                        <div>
                          <p className="font-medium text-gray-900">{org.name}</p>
                          <p className="text-sm text-gray-600">New signup pending approval</p>
                        </div>
                        <Badge variant="outline" className="bg-yellow-100 text-yellow-800">
                          Pending
                        </Badge>
                      </div>
                    ))}
                    {activeOrgs.slice(0, 2).map(org => (
                      <div key={org.id} className="flex items-center justify-between p-3 bg-green-50 rounded-lg border border-green-200">
                        <div>
                          <p className="font-medium text-gray-900">{org.name}</p>
                          <p className="text-sm text-gray-600">
                            {org.stats?.teamMembers} team_members, {org.stats?.assets} assets, {org.stats?.trips} trips
                          </p>
                        </div>
                        <Badge className="bg-green-600">Active</Badge>
                      </div>
                    ))}
                    {organizations.length === 0 && (
                      <p className="text-center text-gray-500 py-4">No activity yet</p>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Accounts View */}
          {activeView === 'accounts' && (
            <div className="space-y-6">
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-1">Asset Accounts</h2>
                <p className="text-gray-600">Manage all organizations</p>
              </div>

              {/* Pending Approvals */}
              {pendingOrgs.length > 0 && (
                <Card className="border-yellow-200">
                  <CardHeader className="bg-yellow-50">
                    <CardTitle className="flex items-center gap-2 text-yellow-800">
                      <Clock className="w-5 h-5" />
                      Pending Approvals ({pendingOrgs.length})
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-6">
                    <div className="space-y-4">
                      {pendingOrgs.map(org => (
                        <div key={org.id} className="border border-gray-200 rounded-lg p-4 bg-white">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <h3 className="text-lg font-semibold text-gray-900">{org.name}</h3>
                              <p className="text-sm text-gray-600 mt-1">
                                Created: {new Date(org.createdAt).toLocaleDateString()}
                              </p>
                              <div className="mt-3 grid grid-cols-2 gap-3">
                                <div>
                                  <p className="text-xs text-gray-500">Admin Name</p>
                                  <p className="text-sm font-medium">{org.adminName || 'N/A'}</p>
                                </div>
                                <div>
                                  <p className="text-xs text-gray-500">Email</p>
                                  <p className="text-sm font-medium">{org.adminEmail || 'N/A'}</p>
                                </div>
                                <div>
                                  <p className="text-xs text-gray-500">Subscription Tier</p>
                                  <Badge variant="outline" className="mt-1 capitalize">
                                    {org.subscriptionTier}
                                  </Badge>
                                </div>
                              </div>
                              
                              {selectedOrg === org.id && (
                                <div className="mt-4 space-y-2">
                                  <label className="text-sm font-medium">Database Connection String</label>
                                  <Input
                                    placeholder="mongodb://localhost:27017/org_database"
                                    value={dbConnectionString}
                                    onChange={(e) => setDbConnectionString(e.target.value)}
                                  />
                                  <p className="text-xs text-gray-500">
                                    Enter the MongoDB connection string for this organization
                                  </p>
                                </div>
                              )}
                            </div>
                            <div className="flex gap-2 ml-4">
                              {selectedOrg === org.id ? (
                                <>
                                  <Button
                                    size="sm"
                                    className="bg-green-600 hover:bg-green-700"
                                    onClick={() => approveOrganization(org.id)}
                                  >
                                    <CheckCircle className="w-4 h-4 mr-1" />
                                    Confirm
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => {
                                      setSelectedOrg(null);
                                      setDbConnectionString('');
                                    }}
                                  >
                                    Cancel
                                  </Button>
                                </>
                              ) : (
                                <>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="text-green-600 border-green-600 hover:bg-green-50"
                                    onClick={() => setSelectedOrg(org.id)}
                                  >
                                    Approve
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="text-red-600 border-red-600 hover:bg-red-50"
                                    onClick={() => rejectOrganization(org.id)}
                                  >
                                    Reject
                                  </Button>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Active Organizations */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Building2 className="w-5 h-5" />
                    Active Organizations ({activeOrgs.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {activeOrgs.map(org => (
                      <div key={org.id} className="border border-gray-200 rounded-lg p-4 hover:border-blue-300 transition-colors">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-3">
                              <h3 className="text-lg font-semibold text-gray-900">{org.name}</h3>
                              <Badge className="bg-green-100 text-green-800">Active</Badge>
                            </div>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                              <div className="flex items-center gap-2">
                                <UserCircle className="w-4 h-4 text-gray-400" />
                                <span className="text-sm text-gray-600">
                                  {org.stats?.team_members || 0} TeamMembers
                                </span>
                              </div>
                              <div className="flex items-center gap-2">
                                <Package className="w-4 h-4 text-gray-400" />
                                <span className="text-sm text-gray-600">
                                  {org.stats?.assets || 0} Assets
                                </span>
                              </div>
                              <div className="flex items-center gap-2">
                                <Calendar className="w-4 h-4 text-gray-400" />
                                <span className="text-sm text-gray-600">
                                  {org.stats?.trips || 0} Bookings
                                </span>
                              </div>
                              <div className="flex items-center gap-2">
                                <CreditCard className="w-4 h-4 text-gray-400" />
                                <span className="text-sm text-gray-600 capitalize">
                                  {org.subscriptionTier}
                                </span>
                              </div>
                            </div>
                            <p className="text-xs text-gray-500 mt-3">
                              Approved: {org.approvedAt ? new Date(org.approvedAt).toLocaleDateString() : 'N/A'}
                            </p>
                          </div>
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-orange-600 border-orange-600 hover:bg-orange-50"
                            onClick={() => suspendOrganization(org.id)}
                          >
                            Suspend
                          </Button>
                        </div>
                      </div>
                    ))}
                    {activeOrgs.length === 0 && (
                      <p className="text-center text-gray-500 py-8">No active organizations yet</p>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Suspended Organizations */}
              {suspendedOrgs.length > 0 && (
                <Card className="border-orange-200">
                  <CardHeader className="bg-orange-50">
                    <CardTitle className="flex items-center gap-2 text-orange-800">
                      <XCircle className="w-5 h-5" />
                      Suspended Organizations ({suspendedOrgs.length})
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-6">
                    <div className="space-y-4">
                      {suspendedOrgs.map(org => (
                        <div key={org.id} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                          <div className="flex items-center justify-between">
                            <div>
                              <h3 className="text-lg font-semibold text-gray-900">{org.name}</h3>
                              <p className="text-sm text-gray-600 mt-1">
                                {org.adminEmail}
                              </p>
                            </div>
                            <Badge variant="outline" className="bg-orange-100 text-orange-800">
                              Suspended
                            </Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          )}

          {/* System Health View */}
          {activeView === 'health' && (
            <SystemHealthView platformHealth={platformHealth} fetchPlatformHealth={fetchPlatformHealth} />
          )}
        </div>
      </main>
    </div>
  );
};

export default SuperAdminDashboard;
